import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Download, Upload, CheckCircle, AlertCircle, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

interface FilaFallida {
  fila: number;
  errores: string[];
}

type EstadoImportacion = 'EN_COLA' | 'PROCESANDO' | 'COMPLETADA' | 'FALLIDA';

interface ImportacionEstado {
  id: string;
  estado: EstadoImportacion;
  totalFilas: number;
  filasProcesadas: number;
  importados: number;
  fallidos: FilaFallida[] | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ProgressBar({ procesadas, total }: { procesadas: number; total: number }) {
  const pct = total > 0 ? Math.round((procesadas / total) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
      <div
        className="bg-primary-500 h-2 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function ImportarPacientesDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [importacionId, setImportacionId] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);
  const avisado = useRef(false);

  function handleClose(v: boolean) {
    onOpenChange(v);
    if (!v) {
      setArchivo(null);
      setImportacionId(null);
      avisado.current = false;
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function descargarPlantilla() {
    setDescargando(true);
    try {
      const res = await api.get('/api/pacientes/plantilla', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla-pacientes.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al descargar la plantilla'));
    } finally {
      setDescargando(false);
    }
  }

  const importarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('archivo', file);
      const res = await api.post<{ id: string; estado: EstadoImportacion }>('/api/pacientes/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      setImportacionId(data.id);
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code ?? err?.response?.data?.message?.code;
      if (code === 'IMPORTACION_EN_PROGRESO') {
        toast.info('Ya hay una importación en curso para esta clínica. Espera a que finalice.');
      } else {
        toast.error(getErrorMessage(err, 'Error al procesar el archivo'));
      }
    },
  });

  const { data: estado } = useQuery<ImportacionEstado>({
    queryKey: ['importacion-pacientes', importacionId],
    queryFn: () => api.get(`/api/pacientes/importaciones/${importacionId}`).then(r => r.data),
    enabled: !!importacionId,
    refetchInterval: (query) => {
      const e = query.state.data?.estado;
      return e === 'EN_COLA' || e === 'PROCESANDO' ? 3000 : false;
    },
  });

  useEffect(() => {
    if (!estado || avisado.current) return;
    if (estado.estado === 'COMPLETADA') {
      avisado.current = true;
      qc.invalidateQueries({ queryKey: ['pacientes'] });
      if (estado.importados > 0) {
        toast.success(`${estado.importados} paciente${estado.importados !== 1 ? 's' : ''} importado${estado.importados !== 1 ? 's' : ''}`);
      }
    } else if (estado.estado === 'FALLIDA') {
      avisado.current = true;
      toast.error('Ocurrió un error al procesar el archivo. Contacta a soporte.');
    }
  }, [estado, qc]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setArchivo(file);
    setImportacionId(null);
    avisado.current = false;
  }

  function handleImportar() {
    if (archivo) importarMutation.mutate(archivo);
  }

  const terminado = estado?.estado === 'COMPLETADA' || estado?.estado === 'FALLIDA';
  const subiendo = importarMutation.isPending;
  // Cubre también el instante entre "ya se subió" y "llegó la primera respuesta del polling"
  // (si solo mirara estado?.estado, ahí no habría ni subiendo ni enProgreso true y la barra parpadearía).
  const enProgreso = subiendo || (!!importacionId && !terminado);

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 focus:outline-none max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-5">
            <Dialog.Title className="text-lg font-semibold text-text-primary">
              Importar pacientes desde Excel
            </Dialog.Title>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </Dialog.Close>
          </div>

          <div className="space-y-5">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-medium text-text-primary mb-1">Paso 1 — Descarga la plantilla</p>
              <p className="text-xs text-text-secondary mb-3">
                Usa nuestra plantilla para asegurarte de que el formato sea correcto. La hoja
                "Instrucciones" explica los valores válidos para cada campo.
              </p>
              <button
                onClick={descargarPlantilla}
                disabled={descargando}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors disabled:opacity-60"
              >
                {descargando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                {descargando ? 'Descargando...' : 'Descargar plantilla (.xlsx)'}
              </button>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-medium text-text-primary mb-1">Paso 2 — Sube tu archivo</p>
              <p className="text-xs text-text-secondary mb-3">
                Selecciona el archivo Excel ya completado con los datos de tus pacientes.
              </p>

              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
                className="hidden"
              />

              {archivo ? (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-primary-200 bg-primary-50">
                  <FileSpreadsheet size={20} className="text-primary-500 flex-shrink-0" />
                  <span className="text-sm text-text-primary truncate flex-1">{archivo.name}</span>
                  <button
                    onClick={() => { setArchivo(null); setImportacionId(null); if (inputRef.current) inputRef.current.value = ''; }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => inputRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-6 rounded-lg border-2 border-dashed border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors text-text-secondary hover:text-primary-600"
                >
                  <Upload size={20} />
                  <span className="text-sm">Haz clic para seleccionar un archivo .xlsx</span>
                </button>
              )}
            </div>

            {enProgreso && (
              <div className="rounded-xl border border-gray-100 p-4">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Loader2 size={14} className="animate-spin" />
                  {subiendo && !estado ? 'Subiendo archivo...' : `${estado?.filasProcesadas ?? 0} / ${estado?.totalFilas ?? 0} filas procesadas`}
                </div>
                {estado && <ProgressBar procesadas={estado.filasProcesadas} total={estado.totalFilas} />}
              </div>
            )}

            {estado?.estado === 'COMPLETADA' && (
              <div className="rounded-xl border border-gray-100 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-text-primary">
                    {estado.importados} paciente{estado.importados !== 1 ? 's' : ''} importado{estado.importados !== 1 ? 's' : ''} correctamente
                  </span>
                </div>

                {estado.fallidos && estado.fallidos.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-text-primary">
                        {estado.fallidos.length} fila{estado.fallidos.length !== 1 ? 's' : ''} con errores (no importada{estado.fallidos.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {estado.fallidos.map(f => (
                        <div key={f.fila} className="text-xs bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                          <span className="font-semibold text-amber-700">Fila {f.fila}:</span>
                          <span className="text-amber-600 ml-1">{f.errores.join(' · ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {estado?.estado === 'FALLIDA' && (
              <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded-lg p-3">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>Ocurrió un error al procesar el archivo. Contacta a soporte.</span>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                {terminado ? 'Cerrar' : 'Cancelar'}
              </Dialog.Close>
              {!terminado && (
                <button
                  onClick={handleImportar}
                  disabled={!archivo || enProgreso}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60"
                >
                  {enProgreso && <Loader2 size={14} className="animate-spin" />}
                  {enProgreso ? 'Importando...' : 'Importar pacientes'}
                </button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
